import React, {useEffect, useContext, useState, useRef} from 'react';
import Stack from 'react-bootstrap/Stack';
import { useNavigate } from "react-router-dom";
import { FormContext } from 'context/FormContext';
import { sendSimulationData, getBatteryTypes, getStorageConfigList } from 'services/simulatorApiProxy';
// import ImageLoading from 'assets/images/loading.gif';
import BackgroundDefault from 'assets/images/produkte-e3dc-2026.png';

const PV_MODULE_GEN = 400; // 1 PV Module generates 400W = 0.4 kWp
const PV_MODULE_AREA = 1.85; // 1 PV Module = 1,85m²

const SimulationRequest = () => {

    const { formValues, setFormValues, setResultValues, setBatteryTypes, setStorageConfiguration } = useContext(FormContext);
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(false);
    const [progress, setProgress] = useState(0);
    const didFetch = useRef(false);

    const parseDirection = (direction) => {
        if (direction === 'Nord') {
            return 180;
        }
        if (direction === 'Nordost') {
            return -135;
        }
        if (direction === 'Ost') {
            return -90;
        }
        if (direction === 'Südost') {
            return -45;
        }
        if (direction === 'Süd') {
            return 0;
        }
        if (direction === 'Südwest') {
            return 45;
        }
        if (direction === 'West') {
            return 90;
        }
        if (direction === 'Nordwest') {
            return 135;
        }
        return 0;
    }

    const prepareFormData = () => {

        let data = {
            lat: formValues.Address.LatLng.lat,
            lng: formValues.Address.LatLng.lng,
            postcode: formValues.Address.Zip,
            profiletype: "standardLoadProfile",
            powersource: formValues.SkipRoof ? "AC" : "DC",
            save: 1
        };

        // yearconsumption
        var yearconsumption_kWh = formValues.AnnualConsumption;
        if (formValues.energyUseHeatingPump == 1) {
            yearconsumption_kWh += parseInt(formValues.HeatingPumpConsumption);
        }
        if (formValues.Cars.length > 0) {
            yearconsumption_kWh += formValues.Cars.reduce((sum, value) => {
                const consumptionPer100km =
                    value === 10000 ? 18 :
                    value === 15000 ? 20 :
                    value === 30000 ? 21 : 0;

                return sum + (value * consumptionPer100km) / 100;
            }, 0);
        }
       
        data.yearconsumption = parseInt(yearconsumption_kWh);

        // roof(s)
        data.pvmodules = formValues.Roof.Zones.map(zone => {
            return {                
                "peakpvpower": Math.floor(zone.SquareMeterUsable / PV_MODULE_AREA) * PV_MODULE_GEN,
                "roofslope": zone.Tilt,
                "roofdirection": parseDirection(zone.Direction)
            }
        });

        if (formValues.energyUseHeatingPump || formValues.Cars.length > 0) {
            data.details = 1;
        }

        if (formValues.energyUseHeatingPump) {                        
            data.yearheatconsumption = formValues.HeatingPumpConsumption;  // kWh
            data.heatpumptype = 1; // 1 - Geothermal heat pump, 2 - Air heat pump
            data.maxpowerheatpump = 3800; // leistungsaufnahme der wärmepumpe ~3800 watt
        }

        if (formValues.Cars.length > 0) {
            data.emobility = {
                chargingmode: 1 // 1 - Charging only from solar (PV), 2 - Charging only from grid, 3 - Charging from solar (PV) and battery without grid, 4 - Charging from solar (PV) and battery supported by grid, 5 - Charging from solar (PV) and supported by grid
            }
            data.emobility.ecars = formValues.Cars.map(value => {
                const capacity = value === 10000
                    ? 40
                    : value === 15000
                        ? 70
                        : value === 30000 ? 95 : 0;
                const consumption = value === 10000
                    ? 18
                    : value === 15000
                        ? 20
                        : value === 30000 ? 21 : 0;
                return {
                    ecarbatterycapacity: capacity * 1000, // in Wh
                    ecarconsumption: consumption, // (Wh/100km)
                    ecardistanceinyear: value, // km
                    chargingdays: [
                        "MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"
                    ]
                }
            });
            data.emobility.wallboxes = [
                {
                    ecarratedpowerstation: 0
                }
            ]
        }

        if (formValues.forceSimulationConfig && formValues.storageConfiguration) {
            data.forcesimulationconfig = {
                storagetype: formValues.storageConfiguration.series,
                battcapacity: formValues.storageConfiguration.battery.capacity,
            }
            // reset immediately
            setFormValues(prev => ({
                ...prev,
                forceSimulationConfig: false
            }));
        }

        data.configuratorConfig = JSON.parse(JSON.stringify(formValues));

        // remove heating pump values when no heating pump is selected
        if (formValues.energyUseHeatingPump == 0) {
            delete data.configuratorConfig.UsableSquareMeter;
            delete data.configuratorConfig.HeatingPumpConsumption;
            delete data.configuratorConfig.ConstructionYear;
            delete data.configuratorConfig.RenovationYear;
            delete data.configuratorConfig.EnergyRenovation;
        }

        return data;
    };

    const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    // Make a real request and get data from the API
    useEffect(() => {
        // prevent double api request in dev mode
        if (didFetch.current) return;
        didFetch.current = true;

        const fetchData = async () => {
            try {
                const simulationData = prepareFormData();
                const batteryTypesRequest = getBatteryTypes();
                const storageConfigRequest = getStorageConfigList();

                const [result, batteryTypes, storageConfig] = await Promise.all([
                    sendSimulationData(simulationData),
                    batteryTypesRequest,
                    storageConfigRequest
                ]);  
                
                setProgress(100);
                await sleep(400);

                // remove old product
                if (storageConfig.PROSERIE) {
                    delete storageConfig.PROSERIE;
                }
               
                setBatteryTypes(batteryTypes);
                setStorageConfiguration(storageConfig);
                setResultValues(result);                
            
                navigate('/SimulationResult');
            } catch (error) {
                console.error('Error during request:', error);
                setError(true);
            } finally {
                setIsLoading(false); // Set loading state to false after the request
            }
        };

        fetchData();
    }, [navigate]); // The effect runs only once after the component mounts

    useEffect(() => {
        const duration = 30000; // 30 seconds
        const intervalTime = 100; // update every 100ms
        const increment = 100 / (duration / intervalTime);

        const interval = setInterval(() => {
            setProgress(prev => {
                const next = prev + increment;
                if (next >= 100) {
                clearInterval(interval);
                return 100;
                }
                return next;
            });
        }, intervalTime);

        return () => clearInterval(interval); // cleanup on unmount
    }, []);

    const backgroundStyle = {
        background: `url(${BackgroundDefault}) no-repeat`,
        backgroundSize: '100%',
        backgroundPosition: 'right bottom',
    };

    return (
        <Stack gap={4} className="ContainerPaddingSmall text-center" style={backgroundStyle}>
            <h1>Nur noch wenige Sekunden...</h1>
            <div>Ihre Konfiguration wird finalisiert – gleich sehen Sie Ihr persönliches Ergebnis</div>
            <div className="d-flex justify-content-center">
                {isLoading ? (
                    // <img src={ImageLoading} className="LoadingAnimation" alt="Loading..." width="150" height="150" />
                    <div style={{width: '200px'}}>
                        <div className="progress LoadingAnimation">
                            <div
                                className="progress-bar text-bg-e3dc progress-bar-striped progress-bar-animated" 
                                role="progressbar"
                                style={{ width: `${progress}%` }}
                                aria-valuenow={progress}
                                aria-valuemin="0"
                                aria-valuemax="100"
                            ></div>                            
                        </div>{Math.round(progress)}%
                    </div>
                ) : error ? (
                    <div className="text-danger">Es gab ein Problem mit der Anfrage. Bitte versuchen Sie es später noch einmal.</div>
                ) : (
                    <div>Die Auswertung ist abgeschlossen!</div>
                )}
            </div>
        </Stack>
    )
}

export default SimulationRequest;